import { useState } from 'react';
import { Box, Grid, Typography, Paper, Divider, Button, Avatar, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

// Utility: Normalize object keys to lowercase
const normalizeKeys = (obj) => {
    if (!obj || typeof obj !== 'object') return {};
    return Object.fromEntries(
        Object.entries(obj).map(([key, val]) => [key.toLowerCase(), val])
    );
};

// Size presets for the size breakdown table
const SIZE_PRESETS = {
    male: {
        label: 'Option 1 - Male',
        sizes: ['M', 'L', 'XL', 'XXL', 'XXXL']
    },
    kids: {
        label: 'Option 2 - Kids',
        sizes: ['20', '21', '22', '23', '24', '25', '26', '27', '28']
    },
    female: {
        label: 'Option 3 - Female',
        sizes: ['XS', 'S', 'M', 'L']
    }
};

const Section = ({ title, data, stepIndex, onEditStep }) => {
    // If no data or empty object, only render header
    if (
        data == null ||
        (typeof data === 'object' && Object.keys(data).length === 0)
    ) {
        return (
            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6">{title}</Typography>
                    <Button size="small" variant="outlined" onClick={() => onEditStep(stepIndex)}>
                        Edit
                    </Button>
                </Box>
            </Paper>
        );
    }

    // Normalize client.details sub-object
    const contact = normalizeKeys(data.details);

    return (
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">{title}</Typography>
                <Button size="small" variant="outlined" onClick={() => onEditStep(stepIndex)}>
                    Edit
                </Button>
            </Box>

            {title === 'Client Details' && (
                <>
                    <Divider sx={{ my: 2 }} />

                    {data.companyName && (
                        <Grid item xs={12} sm={6} md={5}>
                            <Typography component="span" variant="body2" color="text.secondary">
                                🏨 Company:&nbsp;
                            </Typography>
                            <Typography component="span" variant="body2" fontWeight="500">
                                {data.companyName}
                            </Typography>
                        </Grid>
                    )}
                    <Grid container spacing={1} mt={0.5}>
                        {data.quotationNumber && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography component="span" variant="body2" color="text.secondary">
                                    Order No.:&nbsp;
                                </Typography>
                                <Typography component="span" variant="body2" fontWeight="500">
                                    {data.quotationNumber}
                                </Typography>
                            </Grid>
                        )}
                        {data.inquiryDate && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography component="span" variant="body2" color="text.secondary">
                                    📅 Date:&nbsp;
                                </Typography>
                                <Typography component="span" variant="body2" fontWeight="500">
                                    {data.inquiryDate}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>

                    <Grid container spacing={1} mt={0.5}>
                        {contact.contactperson && (
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography component="span" variant="body2" color="text.secondary">
                                    👤 Contact:&nbsp;
                                </Typography>
                                <Typography component="span" variant="body2" fontWeight="500">
                                    {contact.contactperson}
                                </Typography>
                            </Grid>
                        )}
                        {contact.mobileno && (
                            <Grid item xs={12} sm={6} md={2}>
                                <Typography component="span" variant="body2" color="text.secondary">
                                    📞 {contact.mobileno}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                    <Grid container spacing={1} mt={0.5}>
                        {contact.email && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography component="span" variant="body2" color="text.secondary">
                                    ✉️ {contact.email}
                                </Typography>
                            </Grid>
                        )}
                        {(contact.city || contact.state) && (
                            <Grid item xs={12} sm={6} md={3}>
                                <Box display="flex" alignItems="center">
                                    {contact.city && (
                                        <Typography variant="body2" color="text.secondary">
                                            🏙️ {contact.city}
                                        </Typography>
                                    )}
                                    {contact.city && contact.state && (
                                        <Typography sx={{ mx: 0.5 }} variant="body2" color="text.secondary">
                                            ,
                                        </Typography>
                                    )}
                                    {contact.state && (
                                        <Typography variant="body2" color="text.secondary">
                                            {contact.state}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </>
            )}
        </Paper>
    );
};

const InquiryReview = ({ formData, onSubmit, onBack, onEditStep, loggedInUser, companyData }) => {
    const { client, product, consigner, transporter, export: exportData } = formData;
    const compState = companyData?.[0]?.State;

    const formatINR = (value) => {
        if (isNaN(value)) return "₹0.00";
        return value.toLocaleString("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
        });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>📋 Review Quotation Summary</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Please review all details before submission. Use the back button to make changes if needed.
            </Typography>

            <Section title="Client Details" data={client} stepIndex={0} onEditStep={onEditStep} />

            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" color="primary">Product Details</Typography>
                    {product?.tableData?.length > 0 && (
                        <Button size="small" variant="outlined" onClick={() => onEditStep(1)}>
                            Edit
                        </Button>
                    )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                {product?.tableData?.length > 0 ? (
                    <Box>
                        <Box sx={{ width: '100%' }}>
                            <DataGrid
                                rows={product.tableData.map((r, i) => ({ ...r, id: r.srNo || i + 1, srNo: r.srNo || i + 1, index: i }))}
                                columns={[
                                    { field: 'srNo', headerName: '#', width: 40, align: 'center', headerAlign: 'center' },
                                    {
                                        field: 'product', headerName: 'Product / Color / Sizes', flex: 1, sortable: false, filterable: false,
                                        renderCell: (params) => {
                                            const r = params.row;
                                            const presetKey = r.sizePresetKey || 'male';
                                            const preset = SIZE_PRESETS[presetKey];
                                            const isSameState = formData?.client?.details?.State === compState;
                                            const rowBreakdown = r.sizeBreakdown || (r.sizeQtys ? { default: r.sizeQtys } : {});
                                            let explicitColors = r.colors || [];
                                            if (!explicitColors.length && r.color && typeof r.color === 'object') {
                                                explicitColors = [r.color];
                                            }
                                            const displayColors = explicitColors.filter(c => c && c.id !== 'default');
                                            const allColorIds = explicitColors.length > 0 ? explicitColors.map(c => c.id) : Object.keys(rowBreakdown);
                                            const sizeTotals = {};
                                            preset.sizes.forEach(sz => {
                                                sizeTotals[sz] = allColorIds.reduce((sum, cId) => sum + Number(rowBreakdown[cId]?.[sz] || 0), 0);
                                            });
                                            const totalQty = Object.values(sizeTotals).reduce((s, v) => s + Number(v), 0);

                                            return (
                                                <Box sx={{ width: '100%', p: 1, display: 'flex', flexDirection: 'column', gap: 1, bgcolor: 'background.default', borderRadius: 1, transition: 'box-shadow 0.2s' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexGrow: 1 }}>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="bold"
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    color: '#1877F2',
                                                                    textDecoration: 'underline',
                                                                    fontSize: '0.9rem',
                                                                    '&:hover': { color: '#1565c0' }
                                                                }}
                                                                onClick={(e) => { e.stopPropagation(); onEditStep(1); }}
                                                            >
                                                                {r.productCode}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{r.description}</Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {isSameState ? `SGST: ₹${formatINR(r.SGSTAmt)} + CGST: ₹${formatINR(r.CGSTAmt)}` : `IGST: ₹${formatINR(r.IGSTAmt)}`}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                                <Chip label={r.unit || 'N/A'} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: 20 }} />
                                                                <Chip label={`${r.tax}% GST`} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.75rem', height: 20 }} />
                                                            </Box>
                                                            {displayColors.length > 0 && (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                                    {displayColors.map(c => (
                                                                        <Avatar
                                                                            key={c.id}
                                                                            sx={{
                                                                                width: 16,
                                                                                height: 16,
                                                                                bgcolor: c.hex,
                                                                                border: (c.hex === '#ffffff' || c.hex === '#ccc') ? '1px solid #cfcfcf' : undefined
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            );
                                        }
                                    },
                                    { field: 'qty', headerName: 'Total Qty', width: 80, align: 'center', headerAlign: 'center' },
                                    { field: 'rate', headerName: 'Rate', width: 100, align: 'right', headerAlign: 'center', renderCell: (params) => formatINR(Number(params.value) || 0) },
                                    { field: 'disc', headerName: 'Disc (%)', width: 60, align: 'center', headerAlign: 'center' },
                                    {
                                        field: 'amount',
                                        headerName: 'Amount ₹',
                                        width: 110,
                                        align: 'right',
                                        headerAlign: 'right',
                                        renderCell: (params) => formatINR(Number(params.row.amount || params.row.TaxableAmt || 0))
                                    }
                                ]}
                                getRowHeight={() => 'auto'}
                                autoHeight
                                hideFooter
                                disableRowSelectionOnClick
                                sx={{
                                    mt: 1,
                                    border: '1px solid #cbd5e1',
                                    borderRadius: 1,
                                    '& .MuiDataGrid-columnHeaders': { minHeight: 36, maxHeight: 36 },
                                    '& .MuiDataGrid-columnHeader': {
                                        backgroundColor: '#f0f0f0',
                                        paddingTop: '4px',
                                        paddingBottom: '4px',
                                        height: 42,
                                        lineHeight: '1.2rem',
                                        fontSize: '13px',
                                    },
                                    '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#edf2feff' },
                                    '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#ffffff' },
                                    '& .MuiDataGrid-row:hover': { backgroundColor: '#f5f5f5' }
                                }}
                            />
                        </Box>

                        {product.tableData?.length > 0 && (
                            <Box mt={2} display="flex" gap={2}>
                                <Box sx={{ flex: '0 0 60%', maxWidth: '60%', minWidth: '40%' }}>
                                    <Box sx={{ overflowX: 'auto', border: '1px solid #cbd5e1', borderRadius: 1, p: 1, bgcolor: '#f9fafb' }}>
                                        {(() => {
                                            const selectedRow = product.tableData[0];
                                            const rowBreakdown = selectedRow.sizeBreakdown || (selectedRow.sizeQtys ? { default: selectedRow.sizeQtys } : {});
                                            const preset = SIZE_PRESETS[selectedRow.sizePresetKey || 'male'];
                                            const num_sizes = preset.sizes.length;
                                            const total_units = 3 + num_sizes; // 2 for color + num_sizes * 1 + 1 for total
                                            const multiplier = 12 / total_units;
                                            const color_xs = 2 * multiplier;
                                            const size_xs = 1 * multiplier;
                                            const total_xs = 1 * multiplier;
                                            const breakdownKeys = Object.keys(rowBreakdown);
                                            if (breakdownKeys.length === 0) {
                                                return <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>No size breakdown available.</Typography>;
                                            }
                                            return (
                                                <Grid container sx={{ border: '1px solid #ddd', borderRadius: 1 }}>
                                                    {/* Header Row */}
                                                    <Grid item xs={color_xs} sx={{ borderBottom: '1px solid #ddd', p: 1, fontWeight: 'bold' }}>
                                                        <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Color</Typography>
                                                    </Grid>
                                                    {preset.sizes.map(s => (
                                                        <Grid item xs={size_xs} key={s} sx={{ borderBottom: '1px solid #ddd', borderLeft: '1px solid #ddd', p: 1, textAlign: 'center', fontWeight: 'bold' }}>
                                                            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{s}</Typography>
                                                        </Grid>
                                                    ))}
                                                    <Grid item xs={total_xs} sx={{ borderBottom: '1px solid #ddd', borderLeft: '1px solid #ddd', p: 1, textAlign: 'center', fontWeight: 'bold' }}>
                                                        <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Total</Typography>
                                                    </Grid>
                                                    {/* Data Rows */}
                                                    {breakdownKeys.map(cId => {
                                                        const colorObj = cId === 'default' ? { name: 'Default', hex: '#ccc' } : (selectedRow.colors?.find(c => c.id === cId) || { name: cId, hex: '#ccc' });
                                                        const rowTotal = preset.sizes.reduce((sum, s) => sum + Number(rowBreakdown[cId][s] || 0), 0);
                                                        return (
                                                            <Grid container key={cId}>
                                                                <Grid item xs={color_xs} sx={{ borderBottom: '1px solid #ddd', p: 1, display: 'flex', alignItems: 'center' }}>
                                                                    <Avatar sx={{ mr: 1, width: 20, height: 20, bgcolor: colorObj.hex, border: colorObj.hex === '#ffffff' ? '1px solid #cfcfcf' : undefined }} />
                                                                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{colorObj.name}</Typography>
                                                                </Grid>
                                                                {preset.sizes.map(s => (
                                                                    <Grid item xs={size_xs} key={s} sx={{ borderBottom: '1px solid #ddd', borderLeft: '1px solid #ddd', p: 1, textAlign: 'center' }}>
                                                                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{rowBreakdown[cId][s] || 0}</Typography>
                                                                    </Grid>
                                                                ))}
                                                                <Grid item xs={total_xs} sx={{ borderBottom: '1px solid #ddd', borderLeft: '1px solid #ddd', p: 1, textAlign: 'center' }}>
                                                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{rowTotal}</Typography>
                                                                </Grid>
                                                            </Grid>
                                                        );
                                                    })}
                                                    {/* Total Row */}
                                                    <Grid container sx={{ bgcolor: 'grey.100' }}>
                                                        <Grid item xs={color_xs} sx={{ p: 1, fontWeight: 'bold' }}>
                                                            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Total</Typography>
                                                        </Grid>
                                                        {preset.sizes.map(s => {
                                                            const colTotal = breakdownKeys.reduce((sum, cId) => sum + Number(rowBreakdown[cId]?.[s] || 0), 0);
                                                            return (
                                                                <Grid item xs={size_xs} key={s} sx={{ borderLeft: '1px solid #ddd', p: 1, textAlign: 'center', fontWeight: 'bold' }}>
                                                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{colTotal}</Typography>
                                                                </Grid>
                                                            );
                                                        })}
                                                        <Grid item xs={total_xs} sx={{ borderLeft: '1px solid #ddd', p: 1, textAlign: 'center', fontWeight: 'bold' }}>
                                                            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                                {breakdownKeys.reduce((sum, cId) => sum + preset.sizes.reduce((s, sz) => s + Number(rowBreakdown[cId][sz] || 0), 0), 0)}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            );
                                        })()}
                                    </Box>
                                </Box>
                                <Box sx={{ flex: '0 0 40%', maxWidth: '40%' }}>
                                    <Box sx={{ border: '1px solid #cbd5e1', borderRadius: 1, p: 1, minWidth: 280, backgroundColor: '#f9fafb', marginRight: 2 }}>
                                        {(() => {
                                            const s = product?.summary || {};
                                            const items = [{ label: 'Basic Amount:', value: `₹${(s.basic || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` }];

                                            if (s.igst > 0) {
                                                items.push({ label: 'IGST:', value: `₹${s.igst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` });
                                            } else {
                                                if (s.cgst > 0) {
                                                    items.push({ label: 'CGST:', value: `₹${s.cgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` });
                                                }
                                                if (s.sgst > 0) {
                                                    items.push({ label: 'SGST:', value: `₹${s.sgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` });
                                                }
                                            }

                                            items.push(
                                                { label: 'Round Off:', value: `₹${(s.roundOff || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` },
                                                { label: 'Grand Total:', value: `₹${(s.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` }
                                            );

                                            return items.map((item, idx) => (
                                                <Box
                                                    key={idx}
                                                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
                                                >
                                                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                                                        {item.value}
                                                    </Typography>
                                                </Box>
                                            ));
                                        })()}
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Typography color="text.secondary">No product data available.</Typography>
                )}
            </Paper>

            <Box mt={3} display="flex" justifyContent="space-between">
                <Button variant="outlined" onClick={onBack}>Back</Button>
                <Button variant="contained" color="primary" onClick={onSubmit}>Submit</Button>
            </Box>
        </Box>
    );
};

export default InquiryReview;